/**
 * Cloudhouse™ — Backstage UI theme
 * @module theme/cloudhouseTheme
 *
 * Cloudhouse corporate palette applied to Backstage via @backstage/theme.
 * Navy is the dominant surface; green is the single accent. See the
 * Cloudhouse Brand Guidelines 2025 for the authoritative values.
 */
import {
  createUnifiedTheme,
  palettes,
  type UnifiedTheme,
} from '@backstage/theme';

// Brand palette — exact hex values, do not approximate.
const ch = {
  navy: '#0D2839',
  green: '#3BD481',
  white: '#FFFFFF',
  grey: '#F2F2F2',
  orange: '#FF751F',
  lightBlue: '#00B5F8',
  purple: '#8546F8',
} as const;

// Card/raised surface on dark — just above the navy body so cards sit close
// to the background and the hairline border does the lift (cloudhouse.com hero).
const navyPaper = '#0F2E40';

// Open Sans is the brand standard; fall back through the brand-approved
// Helvetica stack. To ship the actual webfont, add `@fontsource/open-sans`
// and import it in packages/app/src/index.tsx — the stack below is the
// approved fallback until then.
const fontFamily = '"Open Sans", "Helvetica Neue", Helvetica, Arial, sans-serif';

// Flat navy header — the banner/nav stays solid navy; the ambient green glow
// lives on the page body (see cloudhouse.css), not here. Built directly (not
// via genPageTheme) to avoid the wave SVG overlay. White title text.
const chPageTheme = {
  colors: [ch.navy],
  shape: 'none',
  backgroundImage: `linear-gradient(90deg, ${ch.navy}, ${ch.navy})`,
  fontColor: ch.white,
};

const pageTheme = {
  home: chPageTheme,
  documentation: chPageTheme,
  tool: chPageTheme,
  service: chPageTheme,
  website: chPageTheme,
  library: chPageTheme,
  other: chPageTheme,
  app: chPageTheme,
  apis: chPageTheme,
};

/**
 * Dark theme — navy surfaces, green accent. This is the primary Cloudhouse
 * presentation (navy is 80%+ of all surfaces).
 */
export const cloudhouseDark: UnifiedTheme = createUnifiedTheme({
  fontFamily,
  defaultPageTheme: 'home',
  pageTheme,
  palette: {
    ...palettes.dark,
    // Green reads well on navy, so it leads on dark.
    primary: { main: ch.green, dark: '#2bbf6e', light: '#6fe0a3' },
    secondary: { main: ch.lightBlue },
    error: { main: '#d32f2f' },
    warning: { main: ch.orange },
    info: { main: ch.lightBlue },
    success: { main: ch.green },
    background: {
      default: ch.navy,
      paper: navyPaper,
    },
    banner: {
      info: ch.lightBlue,
      error: '#d32f2f',
      text: ch.white,
      link: ch.green,
    },
    navigation: {
      background: ch.navy,
      indicator: ch.green,
      color: '#b3c2c9',
      selectedColor: ch.white,
      navItem: { hoverBackground: navyPaper },
      submenu: { background: navyPaper },
    },
  },
  components: {
    // Hairline border lifts cards off the navy body (cloudhouse.com hero look).
    MuiCard: {
      styleOverrides: {
        root: { border: '1px solid rgba(255, 255, 255, 0.08)' },
      },
    },
  },
});

/**
 * Light theme — white/grey surfaces. Navy leads interactive elements here
 * because green lacks contrast on white; green stays as the page-header accent.
 */
export const cloudhouseLight: UnifiedTheme = createUnifiedTheme({
  fontFamily,
  defaultPageTheme: 'home',
  pageTheme,
  palette: {
    ...palettes.light,
    primary: { main: ch.navy, light: '#2c5066', dark: '#081a26' },
    secondary: { main: '#2bbf6e' },
    error: { main: '#d32f2f' },
    warning: { main: ch.orange },
    info: { main: '#0090c8' },
    success: { main: '#2bbf6e' },
    background: {
      default: ch.grey,
      paper: ch.white,
    },
    banner: {
      info: ch.lightBlue,
      error: '#d32f2f',
      text: ch.navy,
      link: ch.navy,
    },
    navigation: {
      background: ch.navy,
      indicator: ch.green,
      color: '#b3c2c9',
      selectedColor: ch.white,
      navItem: { hoverBackground: navyPaper },
      submenu: { background: navyPaper },
    },
  },
  components: {
    // Subtle border for definition on the light grey background.
    MuiCard: {
      styleOverrides: {
        root: { border: '1px solid rgba(0, 0, 0, 0.08)' },
      },
    },
  },
});
